// Invite API client —— 包装 /auth/invites/* + /api/i/* 调用。
// 设计：docs/I1-invite-design.html §5

import { inferAuthBase } from "./auth-api";

export interface InvitePreviewValid {
  valid: true;
  kind: "use" | "room";
  room_id: string | null;
  room_name: string | null;
  issuer_name: string | null;
  expires_at: number;
  remaining_uses: number;
}
export interface InvitePreviewInvalid {
  valid: false;
  reason: string;
}
export type InvitePreview = InvitePreviewValid | InvitePreviewInvalid;

export interface CreateInvitePayload {
  kind: "use" | "room";
  room_id?: string;
  ttl_seconds?: number;
  max_uses?: number;
  note?: string;
}

export interface InviteSummary {
  token: string;
  url: string;
  kind: string;
  room_id: string | null;
  created_at: number;
  expires_at: number;
  max_uses: number;
  used_count: number;
  revoked: boolean;
  note: string | null;
}

export interface RedeemResult {
  kind: "use" | "room";
  room_id: string | null;
  joined: boolean;
}

export class InviteApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly serverMessage: string,
  ) {
    super(`invite api ${status}: ${serverMessage}`);
  }
}

async function call<T>(path: string, init: RequestInit): Promise<T> {
  const base = inferAuthBase();
  const res = await fetch(`${base}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init.headers || {}),
    },
  });
  const text = await res.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    /* not JSON */
  }
  if (!res.ok) {
    const msg =
      body && typeof body === "object" && "error" in body
        ? String((body as { error: unknown }).error)
        : text || res.statusText;
    throw new InviteApiError(res.status, msg);
  }
  return body as T;
}

function authHeader(session: string): Record<string, string> {
  return { Authorization: `Bearer ${session}` };
}

// ---------- 无认证 ----------

export function previewInvite(token: string): Promise<InvitePreview> {
  return call<InvitePreview>(`/api/i/${encodeURIComponent(token)}/preview`, {
    method: "GET",
  });
}

// ---------- 需 Bearer ----------

export function redeemInvite(
  token: string,
  session: string,
): Promise<RedeemResult> {
  return call<RedeemResult>(`/api/i/${encodeURIComponent(token)}/redeem`, {
    method: "POST",
    headers: authHeader(session),
  });
}

export interface CreatedInvite {
  token: string;
  url: string;
  kind: string;
  room_id: string | null;
  expires_at: number;
  max_uses: number;
  note: string | null;
}

export function createInvite(
  payload: CreateInvitePayload,
  session: string,
): Promise<CreatedInvite> {
  return call<CreatedInvite>("/auth/invites", {
    method: "POST",
    headers: authHeader(session),
    body: JSON.stringify(payload),
  });
}

export function listInvites(
  session: string,
): Promise<{ invites: InviteSummary[] }> {
  return call<{ invites: InviteSummary[] }>("/auth/invites", {
    method: "GET",
    headers: authHeader(session),
  });
}

export function revokeInvite(
  token: string,
  session: string,
): Promise<{ ok: boolean; token: string }> {
  return call<{ ok: boolean; token: string }>(
    `/auth/invites/${encodeURIComponent(token)}`,
    {
      method: "DELETE",
      headers: authHeader(session),
    },
  );
}

export interface RedemptionRow {
  redeemed_by_user: string;
  redeemed_email: string | null;
  redeemed_display_name: string | null;
  redeemed_at: number;
  ip_hash: string | null;
}

export function listRedemptions(
  token: string,
  session: string,
): Promise<{ redemptions: RedemptionRow[] }> {
  return call<{ redemptions: RedemptionRow[] }>(
    `/auth/invites/${encodeURIComponent(token)}/redemptions`,
    {
      method: "GET",
      headers: authHeader(session),
    },
  );
}

// ---------- pending invite（受邀人未登录时存 sessionStorage）----------

const PENDING_KEY = "chatbox.pending_invite";

export function setPendingInvite(token: string): void {
  try {
    sessionStorage.setItem(PENDING_KEY, token);
  } catch {
    /* private mode */
  }
}
export function takePendingInvite(): string | null {
  try {
    const v = sessionStorage.getItem(PENDING_KEY);
    if (v) sessionStorage.removeItem(PENDING_KEY);
    return v;
  } catch {
    return null;
  }
}
